// client/src/components/common/NotificationButton.jsx - WITH APPOINTMENT ENHANCEMENTS
import React, { useState, useEffect, useRef } from 'react';
import { format, formatDistance } from 'date-fns';
import notificationService from '../../services/notificationService';
import '../../styles/others/NotificationButton.css';

const NotificationButton = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Force socket initialization on component mount
    const socket = require('../../services/socketService').initializeSocket();
    console.log('Socket initialized in NotificationButton:', socket?.id, socket?.connected);
  }, []);

  // Fetch notifications
  // In NotificationButton.jsx
  const fetchNotifications = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      console.log('Fetching notifications, page:', pageNum);
      
      const response = await notificationService.getNotifications(pageNum, 10);
      console.log('API Response:', response);
      
      // Handle different response formats
      let notificationData = [];
      let paginationData = { pages: 1 };
      
      if (response.success && Array.isArray(response.data)) {
        notificationData = response.data;
        paginationData = response.pagination || { pages: 1 };
      } else if (Array.isArray(response)) {
        notificationData = response;
      } else if (response.data && Array.isArray(response.data)) {
        notificationData = response.data;
      }
      
      console.log(`Parsed ${notificationData.length} notifications from response`);
      
      setNotifications(prev => append ? [...prev, ...notificationData] : notificationData);
      setHasMore(pageNum < (paginationData.pages || 1));
      
      // Update unread count
      const unread = notificationData.filter(n => !n.read).length;
      if (!append) {
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const counts = await notificationService.getNotificationCount();
      setUnreadCount(counts.unread);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const testAPIConnection = async () => {
    try {
      console.log('Testing notification API connection...');
      const count = await notificationService.getNotificationCount();
      console.log('API test successful:', count);
      return true;
    } catch (error) {
      console.error('API test failed:', error);
      return false;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    testAPIConnection();
  }, []);

  // Initialize and subscribe to socket events
useEffect(() => {
  // Fetch initial count
  fetchNotificationCount();

  // Subscribe to real-time updates with improved handlers
  const unsubscribe = notificationService.subscribeToNotifications({
    onNewNotification: (notification) => {
      console.log('New notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification._id
        });
      }
    },
    onCountUpdate: (counts) => {
      console.log('Count update received:', counts);
      if (counts && typeof counts.unread === 'number') {
        setUnreadCount(counts.unread);
      } else {
        fetchNotificationCount(); // Fallback to API call if data format is unexpected
      }
    },
    onNewMessage: ({ notification }) => {
      console.log('New message notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onAppointmentUpdate: (notification) => {
      console.log('Appointment update notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound for appointment reminders
      if (notification.data?.notificationType === 'reminder' || 
          notification.data?.notificationType === 'upcoming') {
        playNotificationSound();
      }
    },
    // NEW: Add specific handler for appointment requests
    onAppointmentRequest: (data) => {
      console.log('📋 New appointment request received:', data);
      
      // Create a formatted notification object
      const notification = {
        _id: `appointment_request_${Date.now()}`,
        title: 'New Appointment Request',
        message: data.patientName 
          ? `New appointment request from ${data.patientName}`
          : 'You have a new appointment request',
        type: 'appointment',
        data: {
          appointmentId: data.appointmentId,
          appointmentDate: data.appointmentDate,
          appointmentType: data.appointmentType || 'Consultation',
          notificationType: 'request',
          patientId: data.patientId,
          patientName: data.patientName,
          actionRequired: true
        },
        read: false,
        createdAt: new Date().toISOString()
      };
      
      // Add to notifications
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound for appointment requests
      playNotificationSound();
      
      // Show browser notification
      if (Notification.permission === 'granted') {
        new Notification('New Appointment Request', {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification._id,
          requireInteraction: true // Keep notification until user interacts with it
        });
      }
    },
    // NEW: Enhanced appointment notification handler
    onUpcomingAppointment: (data) => {
      console.log('📅 Upcoming appointment notification received:', data);
      
      // Create a formatted notification object
      const notification = {
        _id: `appointment_${Date.now()}`,
        title: 'Upcoming Appointment Reminder',
        message: data.timeToStart === '30 minutes' 
          ? `Your appointment ${data.patientName ? `with ${data.patientName}` : ''} is starting in 30 minutes`
          : `Your appointment ${data.patientName ? `with ${data.patientName}` : ''} is coming up soon`,
        type: 'appointment',
        data: {
          appointmentId: data.appointmentId,
          appointmentDate: data.appointmentDate,
          appointmentType: data.appointmentType,
          meetingLink: data.meetingLink,
          notificationType: 'reminder',
          patientName: data.patientName,
          doctorName: data.doctorName,
          timeToStart: data.timeToStart || '30 minutes'
        },
        read: false,
        createdAt: new Date().toISOString()
      };
      
      // Add to notifications
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound
      playNotificationSound();
      
      // Show browser notification with special icon for appointments
      if (Notification.permission === 'granted') {
        new Notification('Upcoming Appointment', {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification._id,
          requireInteraction: true // Keep notification until user interacts with it
        });
      }
    },
    onMoodCheckIn: (notification) => {
      console.log('Mood check-in notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onSystemNotification: (notification) => {
      console.log('System notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },

    onPaymentProofUploaded: (notification) => {
  console.log('💰 Payment proof notification received:', notification);
  setNotifications(prev => [notification, ...prev]);
  setUnreadCount(prev => prev + 1);
  
  // Play special notification sound for payment proofs
  try {
    const audio = new Audio('/sounds/payment-notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(err => console.error('Error playing payment sound:', err));
  } catch (audioError) {
    console.error('Error creating audio element:', audioError);
    // Fallback to regular notification sound
    playNotificationSound();
  }
  
  // Show browser notification
  if (Notification.permission === 'granted') {
    new Notification('Payment Proof Uploaded', {
      body: notification.message,
      icon: '/favicon.ico',
      tag: notification._id,
      requireInteraction: true
    });
  }
}

  });

  // Request notification permission
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }

  return () => {
    console.log('Cleaning up notification subscriptions');
    unsubscribe();
  };
}, []);


  useEffect(() => {
    // Debug socket connection
    const checkSocketConnection = () => {
      const socket = notificationService.getSocket ? 
        notificationService.getSocket() : null;
      
      if (socket) {
        console.log('Socket connection state:', {
          connected: socket.connected,
          id: socket.id
        });
        
        // Force reconnect if needed
        if (!socket.connected) {
          console.log('Attempting to reconnect socket...');
          socket.connect();
        }
      } else {
        console.warn('No socket instance available');
      }
    };
    
    // Check socket on mount
    checkSocketConnection();
    
    // Set interval to check socket (for debugging)
    const interval = setInterval(checkSocketConnection, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!isOpen) {
      fetchNotifications(1);
      setPage(1);
    }
    setIsOpen(!isOpen);
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Load more notifications
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, true);
  };

  // NEW: Play notification sound
  const playNotificationSound = () => {
    try {
      // Create audio element for notification sound
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.5; // Set volume to 50%
      audio.play().catch(error => {
        console.error('Error playing notification sound:', error);
      });
    } catch (error) {
      console.error('Error creating audio element:', error);
    }
  };

  // Handle notification click
const handleNotificationClick = (notification) => {
  // Mark as read if unread
  if (!notification.read) {
    handleMarkAsRead(notification._id);
  }

  // Navigate based on notification type
  switch (notification.type) {
    case 'message':
      // Navigate to messages/conversation
      window.location.href = `/doctor/messages/${notification.data?.conversationId}`;
      break;
    case 'appointment':
      // Check for payment proof notifications
      if (notification.data?.type === 'payment_proof_uploaded') {
        window.location.href = `/doctor/appointments/payment-confirmation/${notification.data.appointmentId}`;
      } else if (notification.data?.notificationType === 'request') {
        // For appointment requests, go to pending appointments section
        window.location.href = '/doctor/appointments#pending';
      } else if (notification.data?.meetingLink) {
        // For appointments with meeting links, open the meeting
        window.open(notification.data.meetingLink, '_blank');
      } else {
        // Default to appointments page
        window.location.href = '/doctor/appointments';
      }
      break;
    case 'mood':
      // Navigate to patient profile
      window.location.href = `/doctor/patients/${notification.data?.patientId}`;
      break;
    default:
      // Just mark as read
      break;
  }
  
  setIsOpen(false);
};

  // NEW: Render appointment notification specifically
  const renderAppointmentNotification = (notification) => {
    const { data = {} } = notification;
    const { appointmentDate, appointmentType, notificationType, meetingLink, timeToStart } = data;
    
    // Format appointment time if available
    const formattedTime = appointmentDate 
      ? format(new Date(appointmentDate), 'MMM d, yyyy h:mm a')
      : null;
    
    // Determine notification icon based on subtype
    const getIcon = () => {
      switch(notificationType) {
        case 'request': return '📋';
        case 'accepted': return '✅';
        case 'declined': return '❌';
        case 'reminder':
        case 'upcoming': return '⏰';
        case 'rescheduled': return '🔄';
        default: return '📅';
      }
    };

    

  
    
    // Determine notification class based on subtype
    const getNotificationClass = () => {
      switch(notificationType) {
        case 'request': return 'notification-request';
        case 'accepted': return 'notification-accepted';
        case 'declined': return 'notification-declined';
        case 'reminder':
        case 'upcoming': return 'notification-reminder';
        case 'rescheduled': return 'notification-rescheduled';
        default: return '';
      }
    };

  
    
    return (
      <div className={`notification-item ${!notification.read ? 'unread' : ''} ${getNotificationClass()}`}>
        <div className="notification-icon-wrapper">
          <span className="notification-type-icon">{getIcon()}</span>
        </div>
        
        <div className="notification-content">
          <h4 className="notification-title">{notification.title}</h4>
          <p className="notification-message">{notification.message}</p>
          
          {formattedTime && (
            <div className="notification-appointment-details">
              <span className="notification-appointment-time">📅 {formattedTime}</span>
              {appointmentType && (
                <span className="notification-appointment-type">📝 {appointmentType}</span>
              )}
              {timeToStart && (
                <span className="notification-time-remaining">⏱️ Starts in {timeToStart}</span>
              )}
            </div>
          )}
          
          {/* Render meeting join button if available */}
          {meetingLink && (notificationType === 'accepted' || notificationType === 'reminder' || notificationType === 'upcoming') && (
            <div className="notification-actions">
              <a 
                href={meetingLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="notification-join-btn"
                onClick={(e) => e.stopPropagation()}
              >
                Join Meeting
              </a>
            </div>
          )}
          
          <span className="notification-time">
            {notificationService.formatNotificationTime(notification.createdAt)}
          </span>
        </div>
        
        {!notification.read && (
          <div className="notification-unread-dot"></div>
        )}
      </div>
    );
  };


  const renderPaymentProofNotification = (notification) => {
  const { data = {} } = notification;
  
  return (
    <div className={`notification-item ${!notification.read ? 'unread' : ''} notification-payment`}>
      <div className="notification-icon-wrapper">
        <span className="notification-type-icon">💰</span>
      </div>
      
      <div className="notification-content">
        <h4 className="notification-title">{notification.title}</h4>
        <p className="notification-message">{notification.message}</p>
        
        {data.amount && (
          <div className="notification-payment-details">
            <span className="notification-payment-amount">💵 ₱{data.amount}</span>
          </div>
        )}
        
        <div className="notification-actions">
          <button 
            className="notification-view-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/doctor/appointments/payment-confirmation/${data.appointmentId}`;
            }}
          >
            Review Payment
          </button>
        </div>
        
        <span className="notification-time">
          {notificationService.formatNotificationTime(notification.createdAt)}
        </span>
      </div>
      
      {!notification.read && (
        <div className="notification-unread-dot"></div>
      )}
    </div>
  );
};

  return (
    <div className="notification-button-container" ref={dropdownRef}>
      <button
        className={`notification-button ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="notification-dropdown-content">
          {/* Header with mark all as read */}
          <div className="notification-header">
            <h3 className="doctor-notification-title">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="notification-mark-all-btn"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          
          {/* Loading state */}
          {loading && notifications.length === 0 ? (
            <div className="notification-loading">
              <div className="notification-spinner"></div>
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <span className="no-notifications-icon">📭</span>
              <p>No notifications yet</p>
              <p className="no-notifications-subtitle">
                You'll see messages, appointments, and updates here
              </p>
            </div>
          ) : (
            <>
              {/* Notification list with special handling for appointment types */}
              <div className="notification-list">
                {notifications.map(notification => (
                <div key={notification._id} onClick={() => handleNotificationClick(notification)}>
                  {notification.type === 'appointment' && notification.data?.type === 'payment_proof_uploaded'
                    ? renderPaymentProofNotification(notification)
                    : notification.type === 'appointment'
                      ? renderAppointmentNotification(notification)
                      : (
                        <div className={`notification-item ${!notification.read ? 'unread' : ''}`}>
                          <div className="notification-icon-wrapper">
                            <span className="notification-type-icon">
                              {notificationService.getNotificationIcon(notification.type)}
                            </span>
                          </div>

                          <div className="notification-content">
                            <h4 className="notification-title">{notification.title}</h4>
                            <p className="notification-message">{notification.message}</p>
                            <span className="notification-time">
                              {notificationService.formatNotificationTime(notification.createdAt)}
                            </span>
                          </div>

                          {!notification.read && (
                            <div className="notification-unread-dot"></div>
                          )}
                        </div>
                      )
                  }
                </div>
              ))}
              </div>

              {hasMore && (
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;